import { STORAGE_KEYS } from "@/utils/constants.ts";
import { Preferences } from "@capacitor/preferences";

export type StorageKey = keyof typeof STORAGE_KEYS;
// localstorage considered temporary because it can be removed after a few hours ~ days
// for more information https://capacitorjs.com/docs/guides/storage
const TEMPORARY_STORAGE_KEYS: Set<StorageKey> = new Set([
    "GEO_PLUGIN_HTTP_CONFIG",
    "SHOULD_RUN_GEO_PLUGIN_ON_BEFORE_MOUNT"
]);

const isBoolean = (val: string | null) => {
    if (val) {
        return val === 'true' || val === 'false'
    } else {
        return false
    }
}

const isObject = (val: string | null) => {
    if (val) {
        try {
            JSON.parse(val)
            return true
        } catch (e) {
            return false
        }
    } else {
        return false
    }
}

export class StorageService {
    // Get type is string by default, but can be changed to whatever
    // Example:
    //      StorageService.Get<boolean>("...") - return boolean (this is NOT enforced in runtime, you have to make sure it's the actual value's type)
    //      StorageService.Get<string>("...") - return string
    //      StorageService.Get("...") - return string
    // * "true" / "false" will be converted automatically to boolean (true/false)
    // * strings of objects ("{}") will be converted automatically to objects
    static Get<T = string>(key: StorageKey): Promise<T> {
        return new Promise(async resolve => {
            let value: string | null
            if (TEMPORARY_STORAGE_KEYS.has(key)) {
                value = localStorage.getItem(STORAGE_KEYS[key])
            } else {
                const result = await Preferences.get({ key: STORAGE_KEYS[key] })
                value = result.value
            }

            if (isBoolean(value)) {
                resolve(Boolean(value) as T)
            }
            if (isObject(value)) {
                resolve(JSON.parse(value as string) as T)
            }
            resolve(value as T)
        })
    }

    static async Set(key: StorageKey, value: any): Promise<void> {
        const valueToStore = typeof value === 'object' ? JSON.stringify(value) : value;
        if (TEMPORARY_STORAGE_KEYS.has(key)) {
            localStorage.setItem(STORAGE_KEYS[key], valueToStore)
        } else {
            await Preferences.set({ key: STORAGE_KEYS[key], value: valueToStore })
        }
    }

    static async Remove(key: StorageKey): Promise<void> {
        if (TEMPORARY_STORAGE_KEYS.has(key)) {
            localStorage.removeItem(STORAGE_KEYS[key])
        } else {
            await Preferences.remove({ key: STORAGE_KEYS[key] })
        }
    }
}